import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, useAuth } from '../../context/AuthContext';
import { MessageCircle, X, Eye, CheckCircle, XCircle, Search, ChevronDown, Plus, Trash2, Edit } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setStats(data.stats || {});
      
      const pendingRes = await api.get('/admin/users/pending');
      setPendingUsers(pendingRes.data.users || []);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
  };

  const approveUser = async (userId, role = 'BUYER') => {
    try {
      await api.put(`/admin/users/${userId}/approve`, { role });
      loadDashboard();
      alert('Usuario aprobado exitosamente');
    } catch (err) {
      alert('Error al aprobar usuario');
    }
  };

  const rejectUser = async (userId) => {
    if (!confirm('¿Rechazar este usuario?')) return;
    try {
      await api.put(`/admin/users/${userId}/reject`, {});
      loadDashboard();
    } catch (err) {
      alert('Error al rechazar usuario');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-black mb-8">Panel de Administrador</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <p className="text-gray-500 text-sm">Total Usuarios</p>
          <p className="text-3xl font-black">{stats.totalUsers || 0}</p>
        </div>
        <div className="bg-primary rounded-xl p-6">
          <p className="text-black text-sm font-medium">Pendientes</p>
          <p className="text-3xl font-black text-black">{stats.pendingUsers || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <p className="text-gray-500 text-sm">Productos</p>
          <p className="text-3xl font-black">{stats.totalProducts || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <p className="text-gray-500 text-sm">Por Aprobar</p>
          <p className="text-3xl font-black text-orange-500">{stats.pendingProducts || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('users')}
          className={`pb-4 px-2 font-semibold whitespace-nowrap ${activeTab === 'users' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          ⏳ Pendientes ({pendingUsers.length})
        </button>
        <button
          onClick={() => setActiveTab('all-users')}
          className={`pb-4 px-2 font-semibold whitespace-nowrap ${activeTab === 'all-users' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          👥 Todos los Usuarios
        </button>
        <button
          onClick={() => setActiveTab('brands')}
          className={`pb-4 px-2 font-semibold whitespace-nowrap ${activeTab === 'brands' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          🏍️ Marcas/Modelos
        </button>
        <button
          onClick={() => setActiveTab('features')}
          className={`pb-4 px-2 font-semibold whitespace-nowrap ${activeTab === 'features' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          ✨ Features
        </button>
        <Link
          to="/admin/products"
          className="pb-4 px-2 font-semibold text-gray-500 whitespace-nowrap"
        >
          📦 Productos
        </Link>
        <button
          onClick={() => setActiveTab('wants')}
          className={`pb-4 px-2 font-semibold whitespace-nowrap ${activeTab === 'wants' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          🔍 Wants
        </button>
        <button
          onClick={() => setActiveTab('subcategories')}
          className={`pb-4 px-2 font-semibold whitespace-nowrap ${activeTab === 'subcategories' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          🏷️ Subcategorías
        </button>
        <Link
          to="/admin/reviews"
          className="pb-4 px-2 font-semibold text-gray-500 whitespace-nowrap"
        >
          ⭐ Reseñas
        </Link>
      </div>

      {/* Pending Users */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-yellow-50">
            <h2 className="text-xl font-bold">Usuarios Pendientes de Aprobación</h2>
            <p className="text-gray-500 text-sm">Revisa y aprueba nuevos registros</p>
          </div>
          
          {pendingUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              ✨ No hay usuarios pendientes
            </div>
          ) : (
            <div className="divide-y">
              {pendingUsers.map(u => (
                <div key={u.id} className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center text-xl font-bold">
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold">{u.name}</p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                      <p className="text-xs text-gray-400">Registrado: {new Date(u.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => approveUser(u.id, 'BUYER')}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600"
                    >
                      ✓ Aprobar Comprador
                    </button>
                    <button
                      onClick={() => approveUser(u.id, 'SELLER')}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600"
                    >
                      ✓ Aprobar Vendedor
                    </button>
                    <button
                      onClick={() => rejectUser(u.id)}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600"
                    >
                      ✕ Rechazar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Users */}
      {activeTab === 'all-users' && (
        <AllUsers />
      )}

      {/* Brands/Categories */}
      {activeTab === 'brands' && (
        <CategoriesManager />
      )}

      {/* Features Management */}
      {activeTab === 'features' && (
        <FeaturesManager />
      )}

      {/* Wants Manager */}
      {activeTab === 'wants' && (
        <AdminWantsManager />
      )}

      {/* Subcategories Manager */}
      {activeTab === 'subcategories' && (
        <SubcategoriesManager />
      )}
    </div>
  );
}

function SubcategoriesManager() {
  const [accessorySubs, setAccessorySubs] = useState([]);
  const [consumableSubs, setConsumableSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAccessory, setNewAccessory] = useState({ id: '', label: '' });
  const [newConsumable, setNewConsumable] = useState({ id: '', label: '' });
  const [editingAcc, setEditingAcc] = useState(null);
  const [editingCons, setEditingCons] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [accRes, consRes] = await Promise.all([
        fetch('/api/accessories/subcategories').then(r => r.json()),
        fetch('/api/consumables/subcategories').then(r => r.json())
      ]);
      setAccessorySubs(Array.isArray(accRes) ? accRes : accRes.subcategories || []);
      setConsumableSubs(Array.isArray(consRes) ? consRes : consRes.subcategories || []);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccessory = async () => {
    if (!newAccessory.label) return;
    const id = newAccessory.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      await api.post('/admin/accessories/subcategories', { id, label: newAccessory.label });
      setNewAccessory({ id: '', label: '' });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar');
    }
  };

  const handleAddConsumable = async () => {
    if (!newConsumable.label) return;
    const id = newConsumable.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    try {
      await api.post('/admin/consumables/subcategories', { id, label: newConsumable.label });
      setNewConsumable({ id: '', label: '' });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar');
    }
  };

  const handleDeleteAccessory = async (id) => {
    if (!confirm('¿Eliminar esta subcategoría?')) return;
    try {
      await api.delete(`/admin/accessories/subcategories/${id}`);
      fetchAll();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const handleDeleteConsumable = async (id) => {
    if (!confirm('¿Eliminar esta subcategoría?')) return;
    try {
      await api.delete(`/admin/consumables/subcategories/${id}`);
      fetchAll();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const handleUpdateAccessory = async (id, newLabel) => {
    try {
      await api.put(`/admin/accessories/subcategories/${id}`, { label: newLabel });
      setEditingAcc(null);
      fetchAll();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const handleUpdateConsumable = async (id, newLabel) => {
    try {
      await api.put(`/admin/consumables/subcategories/${id}`, { label: newLabel });
      setEditingCons(null);
      fetchAll();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  if (loading) return <div className="p-8 text-center">Cargando...</div>;

  return (
    <div className="space-y-8">
      {/* Consumible Subcategories */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <span className="text-2xl">🛢️</span> Tipos de Consumibles
        </h3>
        <p className="text-gray-500 text-sm mb-4">Gestiona los tipos de productos consumibles (llantas, aceite, etc.)</p>
        
        {/* Add form */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newConsumable.label}
            onChange={(e) => setNewConsumable({ ...newConsumable, label: e.target.value })}
            placeholder="Nuevo tipo (ej: Llantas)"
            className="border rounded-lg px-3 py-2 text-sm flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleAddConsumable()}
          />
          <button onClick={handleAddConsumable} className="bg-orange-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600">
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {consumableSubs.map(sub => (
            <div key={sub.id} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
              {editingCons === sub.id ? (
                <>
                  <input
                    type="text"
                    defaultValue={sub.label}
                    className="border rounded px-3 py-1 flex-1"
                    onBlur={(e) => handleUpdateConsumable(sub.id, e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateConsumable(sub.id, e.target.value)}
                    autoFocus
                  />
                  <button onClick={() => setEditingCons(null)} className="text-gray-500 hover:bg-gray-200 px-2 py-1 rounded">Cancelar</button>
                </>
              ) : (
                <>
                  <span className="font-mono text-sm text-gray-500 bg-orange-200 px-2 py-1 rounded">{sub.id}</span>
                  <span className="flex-1 font-medium">{sub.label}</span>
                  <button onClick={() => setEditingCons(sub.id)} className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteConsumable(sub.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
          {consumableSubs.length === 0 && (
            <p className="text-gray-400 text-center py-4">No hay tipos de consumibles configurados</p>
          )}
        </div>
      </div>

      {/* Accessory Subcategories */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <span className="text-2xl">🧤</span> Tipos de Accesorios
        </h3>
        <p className="text-gray-500 text-sm mb-4">Gestiona los tipos de accesorios (cascos, guantes, etc.)</p>
        
        {/* Add form */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newAccessory.label}
            onChange={(e) => setNewAccessory({ ...newAccessory, label: e.target.value })}
            placeholder="Nuevo tipo (ej: Cascos)"
            className="border rounded-lg px-3 py-2 text-sm flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleAddAccessory()}
          />
          <button onClick={handleAddAccessory} className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600">
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {accessorySubs.map(sub => (
            <div key={sub.id} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              {editingAcc === sub.id ? (
                <>
                  <input
                    type="text"
                    defaultValue={sub.label}
                    className="border rounded px-3 py-1 flex-1"
                    onBlur={(e) => handleUpdateAccessory(sub.id, e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateAccessory(sub.id, e.target.value)}
                    autoFocus
                  />
                  <button onClick={() => setEditingAcc(null)} className="text-gray-500 hover:bg-gray-200 px-2 py-1 rounded">Cancelar</button>
                </>
              ) : (
                <>
                  <span className="font-mono text-sm text-gray-500 bg-blue-200 px-2 py-1 rounded">{sub.id}</span>
                  <span className="flex-1 font-medium">{sub.label}</span>
                  <button onClick={() => setEditingAcc(sub.id)} className="text-blue-500 hover:text-blue-700 hover:bg-blue-100 p-2 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteAccessory(sub.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
          {accessorySubs.length === 0 && (
            <p className="text-gray-400 text-center py-4">No hay tipos de accesorios configurados</p>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminWantsManager() {
  const [wants, setWants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [wantsRes, catsRes] = await Promise.all([
        api.get('/admin/wants'),
        api.get('/categories')
      ]);
      setWants(wantsRes.data.wants || []);
      setCategories(catsRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/wants/${id}`, { status });
      loadData();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const updateCategory = async (id, category_id) => {
    try {
      await api.put(`/admin/wants/${id}`, { category_id });
      loadData();
      setEditing(null);
    } catch (err) {
      alert('Error al actualizar categoría');
    }
  };

  const deleteWant = async (id) => {
    if (!confirm('¿Eliminar esta solicitud?')) return;
    try {
      await api.delete(`/admin/wants/${id}`);
      loadData();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const allBrands = [...new Set(wants.map(w => w.category?.brand).filter(Boolean))].sort();

  const filtered = wants.filter(w => {
    const matchSearch = !search || w.title?.toLowerCase().includes(search.toLowerCase()) || w.id?.includes(search);
    const matchStatus = statusFilter === 'all' || w.status === statusFilter;
    const matchBrand = !brandFilter || w.category?.brand === brandFilter;
    return matchSearch && matchStatus && matchBrand;
  });

  const statusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-green-100 text-green-700',
      EXPIRED: 'bg-gray-100 text-gray-600',
      DELETED: 'bg-red-100 text-red-600'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-bold ${styles[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-sm">
        <div className="flex items-center justify-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-500">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-2xl font-black text-gray-900 mb-4">📋 Gestión de Wants</h2>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por título o ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary"
          >
            <option value="">Todas las marcas</option>
            {allBrands.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos los estados</option>
            <option value="ACTIVE">Activos</option>
            <option value="EXPIRED">Expirados</option>
            <option value="DELETED">Eliminados</option>
          </select>
        </div>

        <div className="mt-4 text-sm text-gray-500">
          {filtered.length} solicitud{filtered.length !== 1 ? 'es' : ''} encontrada{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <MessageCircle className="w-16 h-16 mx-auto text-gray-200 mb-4" />
            <p className="text-xl font-semibold text-gray-500">No hay solicitudes</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-bold text-gray-600">Título</th>
                  <th className="text-left px-4 py-3 text-sm font-bold text-gray-600">Usuario</th>
                  <th className="text-left px-4 py-3 text-sm font-bold text-gray-600">Categoría</th>
                  <th className="text-left px-4 py-3 text-sm font-bold text-gray-600">Presupuesto</th>
                  <th className="text-left px-4 py-3 text-sm font-bold text-gray-600">Estado</th>
                  <th className="text-left px-4 py-3 text-sm font-bold text-gray-600">Fecha</th>
                  <th className="text-right px-4 py-3 text-sm font-bold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((want) => (
                  <tr key={want.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="max-w-xs">
                        <p className="font-medium text-gray-900 truncate">{want.title}</p>
                        <p className="text-xs text-gray-400 truncate">{want.id.slice(0, 8)}...</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{want.user?.name || '-'}</td>
                    <td className="px-4 py-3">
                      {editing === want.id ? (
                        <select
                          value={want.category_id || ''}
                          onChange={(e) => updateCategory(want.id, e.target.value || null)}
                          className="text-sm border rounded px-2 py-1 w-full max-w-[150px]"
                          autoFocus
                          onBlur={() => setEditing(null)}
                        >
                          <option value="">Sin categoría</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.brand} {c.model}</option>
                          ))}
                        </select>
                      ) : (
                        <button
                          onClick={() => setEditing(want.id)}
                          className="text-sm text-left hover:bg-gray-100 px-2 py-1 rounded flex items-center gap-1"
                          title="Clic para cambiar categoría"
                        >
                          {want.category ? (
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                              {want.category.brand} {want.category.model}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin categoría</span>
                          )}
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {want.budget_min || want.budget_max
                        ? `₡${(want.budget_min || 0).toLocaleString()} - ₡${(want.budget_max || 0).toLocaleString()}`
                        : '-'}
                    </td>
                    <td className="px-4 py-3">{statusBadge(want.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(want.created_at).toLocaleDateString('es-CR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setSelected(want)}
                          className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {want.status === 'ACTIVE' && (
                          <button
                            onClick={() => updateStatus(want.id, 'EXPIRED')}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title="Expirar"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteWant(want.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Eliminar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold">📋 Detalle del Want</h3>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-bold text-gray-500">Título</label>
                <p className="font-medium">{selected.title}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-500">Descripción</label>
                <p className="text-gray-600">{selected.description || 'Sin descripción'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-gray-500">Usuario</label>
                  <p>{selected.user?.name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-500">Categoría</label>
                  <p>{selected.category ? `${selected.category.brand} ${selected.category.model}` : '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-500">Presupuesto</label>
                  <p>
                    {selected.budget_min || selected.budget_max
                      ? `₡${(selected.budget_min || 0).toLocaleString()} - ₡${(selected.budget_max || 0).toLocaleString()}`
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-500">Provincia</label>
                  <p>{selected.province?.replace('_', ' ') || '-'}</p>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                {selected.status === 'ACTIVE' && (
                  <button
                    onClick={() => { updateStatus(selected.id, 'EXPIRED'); setSelected(null); }}
                    className="flex-1 bg-yellow-100 text-yellow-700 py-2 rounded-lg font-medium hover:bg-yellow-200"
                  >
                    Expirar
                  </button>
                )}
                <button
                  onClick={() => { deleteWant(selected.id); setSelected(null); }}
                  className="flex-1 bg-red-100 text-red-700 py-2 rounded-lg font-medium hover:bg-red-200"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AllUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}/update-role`, { role });
      loadUsers();
    } catch (err) {
      alert('Error al actualizar rol');
    }
  };

  const toggleVerified = async (userId, currentVerified) => {
    try {
      await api.put(`/admin/users/${userId}/verify`, { verified: !currentVerified });
      loadUsers();
    } catch (err) {
      alert('Error al actualizar verificación');
    }
  };

  const updateSubscription = async (userId, plan) => {
    try {
      await api.put(`/admin/users/${userId}/subscription`, { plan });
      loadUsers();
    } catch (err) {
      alert('Error al actualizar suscripción');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING': return <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold">⏳ Pendiente</span>;
      case 'ACTIVE': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">✓ Activo</span>;
      case 'REJECTED': return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">✕ Rechazado</span>;
      default: return status;
    }
  };

  const getSubscriptionBadge = (plan) => {
    switch (plan) {
      case 'PREMIUM': return <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded text-xs font-bold">⭐ PREMIUM</span>;
      case 'PRO': return <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold">PRO</span>;
      default: return <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded text-xs font-bold">FREE</span>;
    }
  };

  if (loading) return <div className="text-center py-8">Cargando...</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b bg-gray-50">
        <h2 className="text-xl font-bold">Todos los Usuarios</h2>
        <p className="text-gray-500 text-sm">Gestiona roles, verificación y suscripciones</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-4 font-semibold">Usuario</th>
              <th className="text-left p-4 font-semibold">Estado</th>
              <th className="text-left p-4 font-semibold">Rol</th>
              <th className="text-left p-4 font-semibold">✓ Verificado</th>
              <th className="text-left p-4 font-semibold">Plan</th>
              <th className="text-left p-4 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center font-bold text-lg">
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">{getStatusBadge(u.status)}</td>
                <td className="p-4">
                  <select
                    value={u.role}
                    onChange={(e) => updateRole(u.id, e.target.value)}
                    className="border rounded-lg px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="BUYER">Comprador</option>
                    <option value="SELLER">Vendedor</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => toggleVerified(u.id, u.is_verified)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      u.is_verified 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                  >
                    {u.is_verified ? '✓ Verificado' : 'No verificado'}
                  </button>
                </td>
                <td className="p-4">
                  <select
                    value={u.subscription_plan || 'FREE'}
                    onChange={(e) => updateSubscription(u.id, e.target.value)}
                    className={`border-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                      u.subscription_plan === 'PREMIUM' ? 'border-orange-400 bg-gradient-to-r from-yellow-50 to-orange-50' :
                      u.subscription_plan === 'PRO' ? 'border-blue-400 bg-blue-50' :
                      'border-gray-300 bg-gray-50'
                    }`}
                  >
                    <option value="FREE">FREE</option>
                    <option value="PRO">PRO</option>
                    <option value="PREMIUM">⭐ PREMIUM</option>
                  </select>
                </td>
                <td className="p-4 text-sm text-gray-400">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeaturesManager() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      const { data } = await api.get('/admin/site/features');
      setFeatures(data.features || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = (id) => {
    const updated = features.map(f => 
      f.id === id ? { ...f, enabled: !f.enabled } : f
    );
    setFeatures(updated);
  };

  const saveFeatures = async () => {
    setSaving(true);
    try {
      await api.put('/admin/site/features', { features });
      alert('Cambios guardados');
    } catch (err) {
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Cargando...</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">✨ Features del Banner</h2>
            <p className="text-gray-500 text-sm">Activa o desactiva los badges en la página principal</p>
          </div>
          <button
            onClick={saveFeatures}
            disabled={saving}
            className="px-6 py-2 bg-primary text-black rounded-lg font-bold hover:bg-orange-600 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : '💾 Guardar Cambios'}
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map(feature => (
            <div 
              key={feature.id} 
              className={`p-4 rounded-xl border-2 transition ${
                feature.enabled 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{feature.icon}</span>
                  <div>
                    <p className="font-bold text-lg">{feature.label}</p>
                    <p className="text-xs text-gray-500">ID: {feature.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature(feature.id)}
                  className={`px-4 py-2 rounded-lg font-bold transition ${
                    feature.enabled 
                      ? 'bg-green-500 text-white hover:bg-green-600' 
                      : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                  }`}
                >
                  {feature.enabled ? '✓ Visible' : '○ Oculto'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <p className="text-sm text-yellow-800">
            <strong>💡 Tip:</strong> Los features deshabilitados no aparecerán en el banner amarillo de la página principal.
          </p>
        </div>
      </div>
    </div>
  );
}

function CategoriesManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newCat, setNewCat] = useState({ brand: '', model: '', year_start: '', year_end: '', cylinder: '' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ brand: '', model: '', year_start: '', year_end: '', cylinder: '' });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/admin/categories');
      setCategories(data.categories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newCat.brand || !newCat.model) return;
    try {
      await api.post('/admin/categories', {
        brand: newCat.brand,
        model: newCat.model,
        year_start: newCat.year_start || null,
        year_end: newCat.year_end || null,
        cylinder: newCat.cylinder || null
      });
      setNewCat({ brand: '', model: '', year_start: '', year_end: '', cylinder: '' });
      setShowAdd(false);
      loadCategories();
      alert('Categoría agregada');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta categoría?\n\nNota: Los productos asociados quedarán sin categoría.')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      loadCategories();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditData({
      brand: cat.brand,
      model: cat.model,
      year_start: cat.year_start || '',
      year_end: cat.year_end || '',
      cylinder: cat.cylinder || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ brand: '', model: '', year_start: '', year_end: '', cylinder: '' });
  };

  const saveEdit = async (id) => {
    if (!editData.brand || !editData.model) {
      alert('Marca y modelo son requeridos');
      return;
    }
    try {
      await api.put(`/admin/categories/${id}`, {
        brand: editData.brand,
        model: editData.model,
        year_start: editData.year_start || null,
        year_end: editData.year_end || null,
        cylinder: editData.cylinder || null
      });
      setEditingId(null);
      loadCategories();
      alert('Categoría actualizada');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar');
    }
  };

  const getBrands = () => [...new Set(categories.map(c => c.brand))].sort();

  if (loading) return <div className="text-center py-8">Cargando...</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-green-50 to-blue-50">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">🏍️ Marcas y Modelos</h2>
            <p className="text-gray-500 text-sm">{categories.length} categorías</p>
          </div>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-orange-600"
          >
            {showAdd ? 'Cancelar' : '+ Nueva Categoría'}
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <input
              type="text"
              placeholder="Marca *"
              value={newCat.brand}
              onChange={(e) => setNewCat({...newCat, brand: e.target.value})}
              className="border rounded-lg px-3 py-2"
            />
            <input
              type="text"
              placeholder="Modelo *"
              value={newCat.model}
              onChange={(e) => setNewCat({...newCat, model: e.target.value})}
              className="border rounded-lg px-3 py-2"
            />
            <input
              type="number"
              placeholder="Año inicio"
              value={newCat.year_start}
              onChange={(e) => setNewCat({...newCat, year_start: e.target.value})}
              className="border rounded-lg px-3 py-2"
            />
            <input
              type="number"
              placeholder="Año fin"
              value={newCat.year_end}
              onChange={(e) => setNewCat({...newCat, year_end: e.target.value})}
              className="border rounded-lg px-3 py-2"
            />
            <input
              type="text"
              placeholder="Cilindraje"
              value={newCat.cylinder}
              onChange={(e) => setNewCat({...newCat, cylinder: e.target.value})}
              className="border rounded-lg px-3 py-2"
            />
          </div>
          <button onClick={handleAdd} className="mt-3 bg-green-500 text-white px-6 py-2 rounded-lg font-bold">
            Agregar
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3 font-bold">Marca</th>
              <th className="text-left p-3 font-bold">Modelo</th>
              <th className="text-left p-3 font-bold">Años</th>
              <th className="text-left p-3 font-bold">Cilindraje</th>
              <th className="text-left p-3 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {categories.map(cat => (
              <tr key={cat.id} className="hover:bg-gray-50">
                {editingId === cat.id ? (
                  <>
                    <td className="p-3"><input type="text" value={editData.brand} onChange={(e) => setEditData({...editData, brand: e.target.value})} className="border rounded px-2 py-1 w-full" /></td>
                    <td className="p-3"><input type="text" value={editData.model} onChange={(e) => setEditData({...editData, model: e.target.value})} className="border rounded px-2 py-1 w-full" /></td>
                    <td className="p-3"><div className="flex gap-1"><input type="number" value={editData.year_start} onChange={(e) => setEditData({...editData, year_start: e.target.value})} className="border rounded px-1 py-1 w-14" placeholder="Ini" /> - <input type="number" value={editData.year_end} onChange={(e) => setEditData({...editData, year_end: e.target.value})} className="border rounded px-1 py-1 w-14" placeholder="Fin" /></div></td>
                    <td className="p-3"><input type="text" value={editData.cylinder} onChange={(e) => setEditData({...editData, cylinder: e.target.value})} className="border rounded px-2 py-1 w-16" placeholder="Cc" /></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <button onClick={() => saveEdit(cat.id)} className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">Guardar</button>
                        <button onClick={cancelEdit} className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-xs font-bold">Cancelar</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-3 font-medium">{cat.brand}</td>
                    <td className="p-3">{cat.model}</td>
                    <td className="p-3 text-sm text-gray-500">{cat.year_start || '?'} - {cat.year_end || '?'}</td>
                    <td className="p-3 text-sm">{cat.cylinder || '-'}</td>
                    <td className="p-3 text-right">
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(cat)} className="text-blue-500 hover:bg-blue-50 px-3 py-1 rounded text-sm font-medium">Editar</button>
                        <button onClick={() => handleDelete(cat.id)} className="text-red-500 hover:bg-red-50 px-3 py-1 rounded text-sm">Eliminar</button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}